// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import {
  databaseAircraftEndpoint,
} from "../core/config.js";
import {
  createCanonicalParserEnvelope,
} from "../core/schema.js";
import {
  monitorLabelForParser as supportedMonitorLabelForParser,
} from "./supported-monitors.js";


export function databaseNormalizedArtifactEndpoint(flightId) {
  return `/api/flights/${encodeURIComponent(flightId)}/normalized-artifact`;
}

export function normalizedArtifactParserEnvelope(artifact) {
  const normalized = artifact?.normalized_json;
  const points = Array.isArray(normalized?.points) ? normalized.points : [];
  if (!points.length) {
    throw new Error("Persisted normalized artifact is missing canonical flight points.");
  }
  return createCanonicalParserEnvelope(points, {
    schemaVersion: normalized.schemaVersion ?? artifact.canonical_schema_version,
    parserId: normalized.parserId ?? artifact.parser_id,
    parserVersion: normalized.parserVersion ?? artifact.parser_version,
    parserConfidence: normalized.parserConfidence ?? artifact.parser_confidence ?? "Medium",
    parserWarnings: normalized.parserWarnings ?? artifact.parse_warnings ?? [],
    engineLayout: normalized.engineLayout,
    ...Object.fromEntries(["engineFamily", "engineMake", "engineModel", "engineCooling", "engineControl"]
      .filter((key) => normalized[key] !== undefined).map((key) => [key, normalized[key]])),
    // Unaligned CGR combo metadata drives the client-side series exclusivity
    // rule and aux-channel analysis strip; dropping it here would silently
    // disable both for persisted flights.
    ...(normalized.cgrTwinAlignment ? { cgrTwinAlignment: normalized.cgrTwinAlignment } : {}),
    ...(normalized.comboAlignment ? { comboAlignment: normalized.comboAlignment } : {}),
    ...(Number.isFinite(normalized.comboOffsetSeconds) ? { comboOffsetSeconds: normalized.comboOffsetSeconds } : {}),
    ...(normalized.sourceData ? { sourceData: normalized.sourceData } : {}),
    ...(Array.isArray(normalized.auxSeriesKeys) ? { auxSeriesKeys: normalized.auxSeriesKeys } : {}),
  });
}

export async function readDatabaseNormalizedArtifactEnvelope(entry, {
  enabled = true,
  fetchImpl = globalThis.fetch,
} = {}) {
  const flightId = entry?.database?.flightId;
  if (!enabled || !flightId || !entry?.database?.normalizedArtifactId) return null;
  const response = await fetchImpl(databaseNormalizedArtifactEndpoint(flightId), { cache: "no-store" });
  if (!response.ok) throw new Error(`Normalized artifact API HTTP ${response.status}`);
  const payload = await response.json();
  return normalizedArtifactParserEnvelope(payload.artifact);
}


export function databaseAircraftLabel(aircraft) {
  return aircraft?.tail_number_normalized
    ?? aircraft?.tail_number
    ?? ([aircraft?.make, aircraft?.model].filter(Boolean).join(" ").trim() || aircraft?.id || "Unknown aircraft");
}

function usableMonitorLabel(value) {
  const label = typeof value === "string" ? value.trim() : "";
  if (!label || label.toLowerCase() === "unknown") return null;
  if (label.toLowerCase() === "garmin user log") return monitorLabelForParser("garmin-user-log");
  return label;
}

export function monitorLabelForParser(parserId) {
  return supportedMonitorLabelForParser(parserId);
}

export function databaseFlightToManifestEntry(flight, aircraft) {
  const metadata = flight?.segment_metadata && typeof flight.segment_metadata === "object" ? flight.segment_metadata : {};
  const monitor = usableMonitorLabel(flight.detected_monitor)
    ?? usableMonitorLabel(metadata.monitor)
    ?? monitorLabelForParser(flight.detected_parser)
    ?? "Unknown";
  return {
    id: flight.id,
    aircraft: databaseAircraftLabel(aircraft),
    path: flight.storage_key,
    title: flight.display_title,
    parser: flight.detected_parser,
    monitor,
    sourceHash: flight.source_sha256,
    original_filename: flight.original_filename ?? null,
    source: "database",
    supported_status: flight.supported_status ?? null,
    unsupported_reason: flight.unsupported_reason ?? null,
    // Per-user unreviewed marker (server-decorated). Only present and true for
    // flights this user has not opened; everything else omits it.
    ...(flight.is_new === true ? { is_new: true } : {}),
    ...(flight.flight_date ? { flight_date: flight.flight_date } : {}),
    ...(flight.flight_start_at ? { flight_start_at: flight.flight_start_at } : {}),
    ...(flight.flight_start_local ? { flight_start_local: flight.flight_start_local } : {}),
    ...(flight.flight_time_source ? { flight_time_source: flight.flight_time_source } : {}),
    ...(Number.isFinite(flight.duration_seconds) ? { duration_seconds: flight.duration_seconds } : {}),
    // Origin/arrival airports (issue #214) so the flights list + PDF + detail can
    // show the "From > To" column without loading points.
    // User-set flight name, shown after the date in the Flights table and in
    // place of the date in the fleet sidebar.
    ...(flight.custom_name ? { custom_name: flight.custom_name } : {}),
    ...(flight.origin_ident ? { origin_ident: flight.origin_ident, origin_kind: flight.origin_kind ?? null } : {}),
    ...(flight.destination_ident ? { destination_ident: flight.destination_ident, destination_kind: flight.destination_kind ?? null } : {}),
    database: {
      aircraftId: flight.aircraft_id,
      uploadId: flight.upload_id,
      flightId: flight.id,
      supportedStatus: flight.supported_status ?? null,
      unsupportedReason: flight.unsupported_reason ?? null,
      normalizedArtifactId: flight.current_normalized_artifact_id ?? null,
      analysisRunId: flight.current_analysis_run_id ?? null,
      ...(flight.current_analysis_findings_summary?.flightFlags
        ? { flagAnalysis: flight.current_analysis_findings_summary.flightFlags }
        : {}),
      compatibilityStatus: flight.compatibility_status ?? null,
      currentEvidencePacketId: flight.current_evidence_packet_id ?? null,
      currentEvidenceSha256: flight.current_evidence_sha256 ?? null,
      aiSummaryId: flight.current_ai_summary_id ?? null,
      aiSummaryEvidencePacketId: flight.ai_summary_evidence_packet_id ?? null,
      aiSummaryGeneratedAt: flight.ai_summary_generated_at ?? null,
      aiSummaryModel: flight.ai_summary_model ?? null,
      aiSummarySchemaVersion: flight.ai_summary_schema_version ?? null,
      aiSummaryGeneratorVersion: flight.ai_summary_generator_version ?? null,
      aiSummaryPromptVersion: flight.ai_summary_prompt_version ?? null,
      aiSummarySourceEvidenceSha256: flight.ai_summary_source_evidence_sha256 ?? null,
      reportId: flight.report_id ?? null,
      reportAnalysisRunId: flight.report_analysis_run_id ?? null,
      reportFileName: flight.report_file_name ?? null,
      reportGeneratedAt: flight.report_generated_at ?? null,
      reportByteSize: Number.isFinite(flight.report_byte_size) ? flight.report_byte_size : null,
      ...(flight.plan_access && typeof flight.plan_access === "object" ? { planAccess: flight.plan_access } : {}),
    },
    ...(metadata.cgrTwin ? { cgrTwin: metadata.cgrTwin } : {}),
    ...(metadata.auxPath ? { auxPath: metadata.auxPath } : {}),
    ...(metadata.pairing ? { pairing: metadata.pairing } : {}),
    ...(metadata.components?.length ? { components: metadata.components } : {}),
    ...(metadata.segment ? { segment: metadata.segment } : {}),
    ...(metadata.jpiFlight ? { jpiFlight: metadata.jpiFlight } : {}),
    ...(metadata.jpiFlightCount ? { jpiFlightCount: metadata.jpiFlightCount } : {}),
    ...(metadata.jpiFlights ? { jpiFlights: metadata.jpiFlights } : {}),
    ...(metadata.sourceAircraft ? { sourceAircraft: metadata.sourceAircraft } : {}),
    ...(metadata.sourceFolderAircraft ? { sourceFolderAircraft: metadata.sourceFolderAircraft } : {}),
  };
}

export async function loadDatabaseFlightManifest({
  fetchImpl = globalThis.fetch,
  aircraftEndpoint = databaseAircraftEndpoint,
  flightsEndpoint = "/api/flights",
} = {}) {
  const aircraftSummaryEndpoint = `${aircraftEndpoint}${aircraftEndpoint.includes("?") ? "&" : "?"}summary=1`;
  const [aircraftResponse, flightsResponse] = await Promise.all([
    fetchImpl(aircraftSummaryEndpoint, { cache: "no-store" }),
    fetchImpl(flightsEndpoint, { cache: "no-store" }),
  ]);
  if (!aircraftResponse.ok) throw new Error(`Database aircraft API HTTP ${aircraftResponse.status}`);
  if (!flightsResponse.ok) throw new Error(`Database flights API HTTP ${flightsResponse.status}`);
  const aircraftPayload = await aircraftResponse.json();
  const flightsPayload = await flightsResponse.json();
  const aircraft = Array.isArray(aircraftPayload.aircraft) ? aircraftPayload.aircraft : [];
  const aircraftById = new Map(aircraft.map((entry) => [entry.id, entry]));
  const flights = (flightsPayload.flights ?? [])
    .map((flight) => {
      const parent = aircraftById.get(flight.aircraft_id);
      return parent ? databaseFlightToManifestEntry(flight, parent) : null;
    })
    .filter(Boolean);
  return { aircraft, flights };
}
